"use client";

import { Play, Eye, ExternalLink, Calendar, Clock, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState } from "react";

import { NUMBER_FORMATTING } from "@/shared/config";
import { Button } from "@/shared/ui/buttons/button";
import { Card } from "@/shared/ui/cards/card";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { Badge } from "@/shared/ui/data-display/badge";

import { VideoPlayerWrapper } from "./VideoPlayerWrapper";
import { formatDateOnlyMsk } from "@/shared/lib/msk-time";

interface StreamData {
  id: string;
  title: string;
  streamer: string;
  streamerAvatar: string;
  thumbnail: string;
  url: string;
  viewers: number;
  duration: string;
  startTime: string;
  platform: "twitch" | "youtube" | "vimeo";
  isLive: boolean;
}

interface VideoPlayerProps {
  stream: StreamData;
  className?: string;
}

export function VideoPlayer({ stream, className = "" }: VideoPlayerProps) {
  const tUi = useTranslations("Ui");
  const [isPlaying, setIsPlaying] = useState(false);
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [_volume] = useState(0.8);

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case "twitch":
        return "bg-purple-100 text-purple-700";
      case "youtube":
        return "bg-red-100 text-red-700";
      case "vimeo":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const formatViewers = (viewers: number) => {
    if (viewers >= NUMBER_FORMATTING.THOUSAND_THRESHOLD) {
      return `${(viewers / NUMBER_FORMATTING.THOUSAND_THRESHOLD).toFixed(NUMBER_FORMATTING.DECIMAL_PLACES)}K`;
    }
    return viewers.toString();
  };

  const handlePlayerReady = () => {
    setPlayerError(null);
  };

  return (
    <Card className={`overflow-hidden ${className}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
        {/* Left side - Information */}
        <div className="lg:col-span-1 p-6 flex flex-col justify-between">
          <div className="space-y-4">
            {/* Title and badges */}
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <h3 className="font-semibold text-lg leading-tight">
                  {stream.title}
                </h3>
                {stream.isLive && (
                  <Badge className="bg-red-500 text-white animate-pulse text-xs">
                    LIVE
                  </Badge>
                )}
              </div>
              <Badge className={getPlatformColor(stream.platform)}>
                {stream.platform.toUpperCase()}
              </Badge>
            </div>

            {/* Streamer Info */}
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage
                  src={stream.streamerAvatar}
                  alt={stream.streamer}
                />
                <AvatarFallback>
                  <Users className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{stream.streamer}</p>
                <p className="text-sm text-muted-foreground">{tUi("videoPlayer.strimer")}</p>
              </div>
            </div>

            {/* Stats */}
            <div className="space-y-2">
              {stream.isLive && (
                <div className="flex items-center gap-2 text-sm">
                  <Eye className="h-4 w-4 text-red-500" />
                  <span className="font-medium">
                    {formatViewers(stream.viewers)} зрителей
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Длительность: {stream.duration}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{formatDateOnlyMsk(new Date(stream.startTime))}</span>
              </div>
            </div>
          </div>

          {/* External Link */}
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-4"
            onClick={() => window.open(stream.url, "_blank")}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Открыть в {stream.platform}
          </Button>
        </div>

        {/* Right side - Video Player */}
        <div className="lg:col-span-2">
          <div className="aspect-video bg-gray-900 relative">
            {!isPlaying ? (
              <>
                <Image
                  src={stream.thumbnail}
                  alt={stream.title}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Button
                    size="lg"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                    onClick={() => setIsPlaying(true)}
                  >
                    <Play className="h-8 w-8 mr-3" />
                    {stream.isLive ? tUi("litVideoPlayer.smotretLive") : tUi("litVideoPlayer.smotret")}
                  </Button>
                </div>
              </>
            ) : playerError ? (
              <div className="flex items-center justify-center h-full bg-gray-900 text-white">
                <div className="text-center">
                  <p className="text-red-400 mb-2">{playerError}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPlayerError(null);
                      setIsPlaying(true);
                    }}
                  >
                    {tUi("videoPlayer.poprobovatSnova")}
                  </Button>
                </div>
              </div>
            ) : (
              <VideoPlayerWrapper
                url={stream.url}
                {...(handlePlayerReady ? { onReady: handlePlayerReady } : {})}
              />
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
